<script setup lang="ts">
import { computed, watch } from "vue";

import { toggleActions } from "@/action/toggleActions";
import SidePanel from "@/components/SidePanel/SidePanel.vue";
import { sendStickyNoteAction } from "@/composables/useEventBus";
import type { CardSearchResult } from "@/model/search";
import { useBoardStore } from "@/store/board";
import { useSearchMenuStore } from "@/store/search/searchMenu";
import { useSearchResultsStore } from "@/store/search/searchResults";

import SearchResults from "./SearchResults.vue";

const resultStore = useSearchResultsStore();

const isPanelOpen = computed(() => useSearchMenuStore().isSearchOpen);

watch(() => isPanelOpen.value, applyStateChange);
watch(() => resultStore.foundIndex, applyCurrentResultChange);

function applyStateChange(isOpen: boolean) {
  if (!isOpen) {
    const id = useBoardStore().enlargedStickyNoteId;
    if (id) sendStickyNoteAction(id, { action: "shrink" });
    resultStore.unsetCurrent();
  }
}

function applyCurrentResultChange(newIndex: number, oldIndex: number) {
  markFound(resultStore.at(oldIndex), false);
  markFound(resultStore.at(newIndex), true);
}

/**
 * Marks or unmarks a card as found
 * @param item The card to enlarge/shrink
 * @param mark Enlarge the sticky if true, shrink if false
 */
function markFound(item: CardSearchResult | undefined, mark: boolean) {
  if (item?.card) {
    if (mark) {
      sendStickyNoteAction(item.id, {
        action: "enlarge",
        trigger: "search-item-selected",
      });
    } else {
      sendStickyNoteAction(item.id, { action: "shrink" });
    }
  }
}

function toggle() {
  toggleActions.toggleSearch("sidebar-menu");
}
</script>

<template>
  <div id="search-results-panel" data-ignore-click>
    <SidePanel side="left" :active="isPanelOpen" name="search" resizable @close="toggle">
      <SearchResults />
    </SidePanel>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/z-index";

#search-results-panel {
  position: relative;
  z-index: z-index.$menu;
}
</style>
